"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.toSecondsFromDurationString = exports.toDurationStringFromSeconds = void 0;
var _toConsumableArray2 = _interopRequireDefault(require("@babel/runtime/helpers/toConsumableArray"));
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
/* eslint-disable import/prefer-default-export */

var durationFormat = Object.freeze({
  days: 3,
  hours: 2,
  minutes: 1
});
var formatTokensCanonicalStrings = Object.freeze({
  [durationFormat.days]: 'days',
  [durationFormat.hours]: 'hours',
  [durationFormat.minutes]: 'minutes'
});
var formatTokensCanonicalShortStrings = Object.freeze({
  [durationFormat.days]: 'd',
  [durationFormat.hours]: 'h',
  [durationFormat.minutes]: 'min'
});
var secondsPer = Object.freeze({
  day: 86400,
  hour: 3600,
  minute: 60
});
var formatTokens = Object.freeze({
  d: durationFormat.days,
  days: durationFormat.days,
  h: durationFormat.hours,
  hours: durationFormat.hours,
  hr: durationFormat.hours,
  hrs: durationFormat.hours,
  m: durationFormat.minutes,
  min: durationFormat.minutes,
  minutes: durationFormat.minutes
});

/**
 * Convert seconds into a user-presentable duration string.
 *
 * @param {Number} value Duration in seconds.
 * @param {Object} opts Conversion options.
 * @returns {string|undefined} Duration string if given value was a number
 *                             If 0 seconds, then it returns an empty string.
 *                             If value is invalid, then undefined is returned.
 */
var toDurationStringFromSeconds = exports.toDurationStringFromSeconds = function toDurationStringFromSeconds(value, opts) {
  if (value === undefined || typeof value !== 'number') {
    return undefined;
  }
  if (value === 0) {
    return '';
  }
  var rem = value;
  var fmt = '';
  if (!(opts != null && opts.excludeDays)) {
    var days = Math.floor(rem / secondsPer.day);
    rem -= days * secondsPer.day;
    if (days) {
      fmt += "".concat(days, " ").concat(formatTokensCanonicalShortStrings[durationFormat.days], " ");
    }
  }
  if (!(opts != null && opts.excludeHours)) {
    var hours = Math.floor(rem / secondsPer.hour);
    rem -= hours * secondsPer.hour;
    if (hours) {
      fmt += "".concat(hours, " ").concat(formatTokensCanonicalShortStrings[durationFormat.hours], " ");
    }
  }

  /* round up, if we had leftover seconds */
  var minutes = Math.ceil(rem / secondsPer.minute);
  rem -= minutes * secondsPer.minute;
  if (minutes) {
    fmt += "".concat(minutes, " ").concat(formatTokensCanonicalShortStrings[durationFormat.minutes], " ");
  }
  if (process.env.NODE_ENV !== 'production' && rem !== 0) {
    console.warn("Remaining seconds not 0 in duration string conversion, rem: ".concat(rem));
  }
  return fmt;
};

/**
 * Convert duration string to seconds.
 * The duration string is may contain excessive whitespace, or no whitespace at all.
 * For example: "1d5h30m" is a valid duration string that will be parsed by this function.
 * Supports parsing of decimals, so entering "0.5 days" is valid.
 * Due to the above feature, it rounds up the parsed value to the nearest minute, so entering "1.01h", which
 * is 1 h and 36 seconds, will be parsed into 3660 seconds, which is 1 h and 1 minute.
 *
 * @param value {String} Input duration string, ex "15h", "8h 20 min", "1        d15m".
 * @returns {Number} Input duration string in seconds on success.
 * @throws Error on invalid input, with the message containing an explanation.
 */
var toSecondsFromDurationString = exports.toSecondsFromDurationString = function toSecondsFromDurationString(value) {
  if (value === undefined || typeof value !== 'string') {
    throw Error('Invalid data');
  }
  if (value.length === 0) {
    return 0;
  }
  var re = /[\d]+((\.|,)[\d]+)?|[a-z]+/gi;
  var tokens = Array.from(value.matchAll(re), function (m) {
    return m[0];
  });
  var parsed = tokens.reduce(function (acc, tok) {
    var num = Number(tok.replace(',', '.'));
    if (!Number.isNaN(num)) {
      if (acc.prevNumber) {
        throw Error("Expected duration, got \"".concat(tok, "\""));
      }
      return (0, _extends2.default)({}, acc, {
        prevNumber: num
      });
    }
    var fmt = formatTokens[tok.toLowerCase()];
    if (fmt) {
      if (acc.prevNumber === undefined) {
        throw Error("Expected number, got \"".concat(tok, "\""));
      }
      if (acc.parsedFormats.includes(fmt)) {
        throw Error("Duration for ".concat(formatTokensCanonicalStrings[fmt], " is duplicated"));
      }
      var seconds = acc.seconds;
      switch (fmt) {
        case durationFormat.days:
          seconds += acc.prevNumber * secondsPer.day;
          break;
        case durationFormat.hours:
          seconds += acc.prevNumber * secondsPer.hour;
          break;
        case durationFormat.minutes:
          seconds += acc.prevNumber * secondsPer.minute;
          break;
        default:
          /* This shouldn't happen */
          throw Error("Invalid duration \"".concat(tok, "\""));
      }
      return (0, _extends2.default)({}, acc, {
        parsedFormats: [].concat((0, _toConsumableArray2.default)(acc.parsedFormats), [fmt]),
        prevNumber: undefined,
        seconds
      });
    }
    throw Error("Invalid duration \"".concat(tok, "\""));
  }, {
    parsedFormats: [],
    prevNumber: undefined,
    seconds: 0
  });
  if (parsed.prevNumber !== undefined) {
    throw Error('Missing duration ("h", "min", etc.)');
  }

  /* Round up to the nearest minute before returning */
  var sRemainder = parsed.seconds % secondsPer.minute;
  var addSeconds = sRemainder > 0 ? secondsPer.minute - sRemainder : 0;
  return parsed.seconds + addSeconds;
};